import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, MapPin, Building2, Loader2, AlertCircle } from 'lucide-react';
import { getClubsByOwner, getClubsByManager } from '@/api/clubs/clubApi';
import { CLUB_FIELDS } from '@/api/clubs/clubs-fields.config';
import { CLUB_STATUS } from '@/types/club.model';
import type { Club } from '@/types/tournament.model';
import type { WizardStepProps, ClubSelectionSlice } from '../../../engine/types';
import { handleGlobalError } from '@/utils/error-handler';
import { cn } from '@/lib/utils';

/**
 * Configuration for the ClubSelectionStep component.
 */
export interface ClubSelectionStepConfig {
  requireCourts?: boolean;
  minCourts?: number;
}

const DEFAULT_CONFIG: ClubSelectionStepConfig = {
  requireCourts: true,
  minCourts: 1,
};

interface ClubSelectionStepProps extends WizardStepProps<ClubSelectionSlice> {
  config?: ClubSelectionStepConfig;
}

export function ClubSelectionStep({
  data,
  updateData,
  config: userConfig,
}: ClubSelectionStepProps) {
  const config = { ...DEFAULT_CONFIG, ...userConfig };
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch clubs on mount
  const fetchClubs = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [ownerClubs, managerClubs] = await Promise.allSettled([
        getClubsByOwner(CLUB_FIELDS.LIST),
        getClubsByManager(CLUB_FIELDS.LIST),
      ]);

      const ownerClubsResult =
        ownerClubs.status === 'fulfilled'
          ? (ownerClubs.value as unknown as Club[])
          : [];

      const managerClubsResult =
        managerClubs.status === 'fulfilled'
          ? (managerClubs.value as unknown as Club[])
          : [];

      // Combine and filter valid clubs
      const allClubs = [...ownerClubsResult, ...managerClubsResult];
      const validClubs = allClubs.filter((club) => {
        const isEnabled = club.status === CLUB_STATUS.ENABLED;
        const hasCourts = config.requireCourts
          ? club.courts && club.courts.length >= (config.minCourts ?? 1)
          : true;
        return isEnabled && hasCourts;
      });

      // Remove duplicates
      const uniqueClubs = validClubs.filter(
        (club, index, self) =>
          index === self.findIndex((c) => c.id === club.id)
      );

      updateData({ clubs: uniqueClubs });
    } catch (err) {
      setError('No se pudieron cargar los clubs');
      handleGlobalError(err, 'Error', 'No se pudieron cargar los clubs');
    } finally {
      setIsLoading(false);
    }
  }, [updateData, config.requireCourts, config.minCourts]);

  useEffect(() => {
    if (!data?.clubs || data.clubs.length === 0) {
      fetchClubs();
    } else {
      setIsLoading(false);
    }
  }, [fetchClubs, data?.clubs]);

  // Filter clubs by search
  const filteredClubs = (data?.clubs ?? []).filter((club) =>
    club.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle club selection
  const handleSelectClub = (club: Club) => {
    updateData({ selectedClub: club });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="mt-4 text-sm text-gray-600">Cargando clubs...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="rounded-full bg-red-100 p-3">
          <AlertCircle className="h-6 w-6 text-red-600" />
        </div>
        <p className="mt-4 text-sm text-red-600">{error}</p>
        <button
          onClick={fetchClubs}
          className="mt-4 text-sm text-blue-600 hover:underline"
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Empty state
  if ((data?.clubs ?? []).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Building2 className="h-12 w-12 text-gray-300" />
        <h3 className="mt-4 text-lg font-medium text-gray-700">
          No tienes clubs disponibles
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Debes tener al menos un club habilitado con canchas para crear un
          torneo.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          placeholder="Buscar club..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Info message */}
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
        <AlertCircle className="h-4 w-4 text-gray-500" />
        <p className="text-xs text-gray-600">
          Solo se muestran clubs habilitados con canchas disponibles
        </p>
      </div>

      {/* Club list */}
      <div className="space-y-4">
        {filteredClubs.map((club) => (
          <ClubCard
            key={club.id}
            club={club}
            isSelected={data?.selectedClub?.id === club.id}
            onSelect={() => handleSelectClub(club)}
          />
        ))}

        {filteredClubs.length === 0 && searchQuery && (
          <p className="py-8 text-center text-gray-500">
            No se encontraron clubs con "{searchQuery}"
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CLUB CARD
// ============================================================================

interface ClubCardProps {
  club: Club;
  isSelected: boolean;
  onSelect: () => void;
}

function ClubCard({ club, isSelected, onSelect }: ClubCardProps) {
  return (
    <Card
      className={cn(
        'cursor-pointer transition-all',
        isSelected
          ? 'border-2 border-blue-500 bg-blue-50 ring-2 ring-blue-200'
          : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
      )}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3
              className={cn(
                'font-semibold',
                isSelected ? 'text-blue-700' : 'text-gray-900'
              )}
            >
              {club.name}
            </h3>

            {club.address && (
              <div className="mt-1 flex items-center gap-1 text-sm text-gray-500">
                <MapPin className="h-3 w-3" />
                <span>
                  {club.address.street}, {club.address.city}
                </span>
              </div>
            )}

            <div className="mt-2 flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {club.courts.length}{' '}
                {club.courts.length === 1 ? 'cancha' : 'canchas'}
              </Badge>
            </div>
          </div>

          {/* Selected indicator */}
          {isSelected && (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500">
              <svg
                className="h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ClubSelectionStep;
